import { z } from 'zod';
import { AlertSeverity } from './alert';

/**
 * 알림 담당자 (53회차).
 *
 * 메일·텔레그램 채널은 "어디로"만이 아니라 **"누구에게"**를 알아야 한다. 그 사람 목록을 관리 화면
 * (관리 → 외부 연동)에서 고치고, 서버가 `{APM_DATA_DIR}/recipients.json`에 둔다.
 *
 * 정한 것 셋.
 *
 * 1. **받는 사람 정보만 담는다.** 이름·이메일·텔레그램 chat id·담당 서비스·심각도·켜짐.
 *    보내는 쪽 자격(텔레그램 봇 토큰, SMTP 계정·비밀번호)은 `.env`에만 있다. 봇 토큰은 봇 하나에
 *    하나이고 사람마다 다른 것은 chat id 라서 그것만 여기 들어온다. 요청 본문에 자격 칸이 오면
 *    모르는 칸으로 거절한다(`strictObject`).
 * 2. **담당 서비스가 비면 전체다.** 서비스가 없는 알림(관제 서버 오류·DB 워처·안 쪼갠 규칙)은
 *    전체 담당자에게만 간다. 서비스를 적은 사람에게 "어느 서비스 것인지 모르는" 알림을 보내지 않는다.
 * 3. **이메일과 chat id 는 둘 중 하나만 있어도 된다.** 있는 쪽으로만 보낸다. 둘 다 없으면 그 줄은
 *    받지 않는다(저장은 된다 — 연락처를 나중에 채우는 경우가 있다).
 *
 * 콘솔이 소스로 소비하므로 `node:*`를 쓰지 않는다.
 */

const epochMs = z.number().int().nonnegative();

/** 이름 상한. 토큰 이름표와 같다 */
export const RECIPIENT_NAME_MAX = 80;
/** 한 사람이 맡을 서비스 수 상한 */
export const RECIPIENT_SERVICES_MAX = 64;

/**
 * 이메일 한 개. 쉼표·세미콜론·꺾쇠·따옴표·괄호·공백을 막는다 — 주소 하나 자리에 여럿을 끼워 넣거나
 * 머리글을 꾸미지 못하게 한다. 형식 검사는 거칠게 두고 실제로 닿는지는 메일 서버가 말한다
 */
export const RECIPIENT_EMAIL = /^[^\s@,;<>"()]+@[^\s@,;<>"()]+\.[^\s@,;<>"()]+$/;

/**
 * 텔레그램 chat id. 개인·그룹은 정수(그룹·채널은 음수), 공개 채널은 `@이름`(5~32자)이다.
 * 봇 토큰(`123:ABC…`)이나 주소를 잘못 붙여 넣으면 여기서 걸린다
 */
export const TELEGRAM_CHAT_ID = /^(-?\d{1,20}|@[A-Za-z][A-Za-z0-9_]{4,31})$/;

const name = z.string().trim().min(1).max(RECIPIENT_NAME_MAX);
/** 빈 문자열은 "없음"이다. 고칠 때 지우는 값으로도 쓴다 */
const email = z
  .string()
  .trim()
  .max(254)
  .refine((value) => value === '' || RECIPIENT_EMAIL.test(value), '이메일 주소 하나만 적는다');
const chatId = z
  .string()
  .trim()
  .refine(
    (value) => value === '' || TELEGRAM_CHAT_ID.test(value),
    '텔레그램 chat id 는 정수이거나 @공개채널 이름이다',
  );
const services = z.array(z.string().trim().min(1).max(200)).max(RECIPIENT_SERVICES_MAX);

/** 담당자 한 줄. 응답과 저장 파일이 같은 모양이다 */
export const AlertRecipient = z.object({
  /** 고치고 지울 때 쓰는 식별자. 서버가 만든다 */
  id: z.string().min(1),
  /**
   * 저장 행도 앞뒤 공백을 떼고 본다 (리뷰 R53-B-1). 공백뿐인 값을 통과시킨 뒤 잘라 버리면 이름이 비거나
   * 담당 서비스가 빈 목록(= 전체 담당)으로 바뀐다
   */
  name: z.string().trim().min(1).max(RECIPIENT_NAME_MAX),
  /** 없으면 메일로 보내지 않는다 */
  email: z.string().regex(RECIPIENT_EMAIL).optional(),
  /** 없으면 텔레그램으로 보내지 않는다 */
  telegramChatId: z.string().regex(TELEGRAM_CHAT_ID).optional(),
  /** 담당 서비스(`service.name` 원문). **비면 전체**다. 공백뿐인 항목이 하나라도 있으면 잘못된 행이다 */
  services: z.array(z.string().trim().min(1)).max(RECIPIENT_SERVICES_MAX),
  /** 이 심각도 이상만 받는다. `warning`이면 둘 다, `critical`이면 심각만 */
  minSeverity: AlertSeverity,
  /** 끄면 지우지 않고 받지 않는다 */
  enabled: z.boolean(),
  createdAt: epochMs,
});
export type AlertRecipient = z.infer<typeof AlertRecipient>;

/** `POST /api/recipients` 본문. 모르는 칸(자격 칸 포함)은 거절한다 */
export const AlertRecipientCreate = z.strictObject({
  name,
  email: email.optional(),
  telegramChatId: chatId.optional(),
  services: services.default([]),
  minSeverity: AlertSeverity.default('warning'),
  enabled: z.boolean().default(true),
});
export type AlertRecipientCreate = z.infer<typeof AlertRecipientCreate>;

/** `PATCH /api/recipients/:id` 본문. 보낸 칸만 고친다. 빈 문자열은 그 연락처를 지운다 */
export const AlertRecipientUpdate = z.strictObject({
  name: name.optional(),
  email: email.optional(),
  telegramChatId: chatId.optional(),
  services: services.optional(),
  minSeverity: AlertSeverity.optional(),
  enabled: z.boolean().optional(),
});
export type AlertRecipientUpdate = z.infer<typeof AlertRecipientUpdate>;

/** `GET`·`POST`·`PATCH`·`DELETE /api/recipients` 응답 */
export const AlertRecipientsResult = z.object({
  /** 만든 순서대로 */
  recipients: z.array(AlertRecipient),
  /**
   * 보내는 쪽 설정이 `.env`에 갖춰져 있는지. **값은 싣지 않는다** — 참/거짓뿐이다.
   * 거짓이면 담당자를 적어도 그 채널로는 나가지 않는다
   */
  senders: z.object({ mail: z.boolean(), telegram: z.boolean() }),
  /** 저장 파일이 깨져 고치기가 막혀 있다. 정상일 때는 실리지 않는다 */
  blocked: z.boolean().optional(),
});
export type AlertRecipientsResult = z.infer<typeof AlertRecipientsResult>;
